import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Card, CardContent
} from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Download, Loader2, RotateCcw } from 'lucide-react';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPES = [
  'ORDER_PAYMENT',
  'RESTAURANT_SETTLEMENT',
  'RESTAURANT_EARNING',
  'DELIVERY_EARNING',
  'DAILY_TARGET_BONUS',
  'COD_COLLECTION'
];

const TYPE_COLORS = {
  ORDER_PAYMENT: 'bg-blue-100 text-blue-700 border-blue-200',
  RESTAURANT_SETTLEMENT: 'bg-purple-100 text-purple-700 border-purple-200',
  RESTAURANT_EARNING: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  DELIVERY_EARNING: 'bg-amber-100 text-amber-700 border-amber-200',
  DAILY_TARGET_BONUS: 'bg-lime-100 text-lime-800 border-lime-200',
  COD_COLLECTION: 'bg-orange-100 text-orange-700 border-orange-200'
};

const STATUSES = ['PENDING', 'SUCCESS', 'FAILED', 'REFUNDED'];

const STATUS_COLORS = {
  SUCCESS: 'bg-green-100 text-green-700 border-green-200',
  PENDING: 'bg-gray-100 text-gray-700 border-gray-200',
  FAILED: 'bg-red-100 text-red-700 border-red-200',
  REFUNDED: 'bg-purple-100 text-purple-700 border-purple-200'
};

const formatINR = (n) =>
  '₹' + new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(Number(n || 0));

const Transactions = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Filter dropdown sources
  const [restaurants, setRestaurants] = useState([]);
  const [users, setUsers] = useState([]);
  const [agents, setAgents] = useState([]);

  // Filters
  const [restaurantId, setRestaurantId] = useState('all');
  const [userId, setUserId] = useState('all');
  const [partnerID, setPartnerID] = useState('all');
  const [type, setType] = useState('all');
  const [status, setStatus] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  // Pagination
  const PAGE_SIZE = 25;
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  useEffect(() => {
    bootstrap();
  }, []);

  useEffect(() => {
    fetchTransactions();
    // eslint-disable-next-line
  }, [restaurantId, userId, partnerID, type, status, from, to, page]);

  const bootstrap = async () => {
    try {
      const [r, u, a] = await Promise.all([
        // This dropdown wants the full restaurant list, not one page of it —
        // ask for a high limit. (Backend now returns { data, pagination, counts }.)
        axios.get(`${API}/restaurants`, { params: { limit: 1000 } }),
        // Same here — this dropdown wants the full user list, not one page.
        // (Backend now returns { data, pagination }.)
        axios.get(`${API}/users`, { params: { limit: 1000 } }),
        axios.get(`${API}/delivery-agents`)
      ]);
      const rd = Array.isArray(r.data?.data) ? r.data.data : (Array.isArray(r.data) ? r.data : []);
      const ud = Array.isArray(u.data?.data) ? u.data.data : (Array.isArray(u.data) ? u.data : u.data?.users || []);
      const ad = Array.isArray(a.data) ? a.data : a.data?.data || a.data?.agents || [];
      setRestaurants(
        rd.map((x) => ({ id: x.id || x._id, name: x.name || '—' }))
      );
      setUsers(
        ud.map((x) => ({
          id: x.id || x._id,
          name: x.name || x.phone || x.email || 'Unnamed',
          phone: x.phone || ''
        }))
      );
      setAgents(
        ad.map((x) => ({
          id: x.id || x._id,
          name: x.name || 'Unnamed',
          phone: x.phone || ''
        }))
      );
    } catch (err) {
      console.error('Failed to bootstrap filters:', err);
    }
  };

  const params = useMemo(() => {
    const p = { skip: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE };
    if (restaurantId !== 'all') p.restaurantId = restaurantId;
    if (userId !== 'all') p.userId = userId;
    if (partnerID !== 'all') p.partnerID = partnerID;
    if (type !== 'all') p.type = type;
    if (status !== 'all') p.status = status;
    if (from) p.from = from;
    if (to) p.to = to;
    return p;
  }, [restaurantId, userId, partnerID, type, status, from, to, page]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/transactions`, { params });
      setRows(res.data?.transactions || []);
      setTotal(Number(res.data?.total || 0));
    } catch (err) {
      console.error(err);
      toast.error('Failed to fetch transactions');
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setRestaurantId('all');
    setUserId('all');
    setPartnerID('all');
    setType('all');
    setStatus('all');
    setFrom('');
    setTo('');
    setPage(1);
  };

  const exportExcel = async () => {
    try {
      setExporting(true);
      const exportParams = { ...params };
      delete exportParams.skip;
      delete exportParams.limit;
      const res = await axios.get(`${API}/transactions/export`, {
        params: exportParams,
        responseType: 'blob'
      });
      const blob = new Blob([res.data], {
        type:
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `transactions-${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Exported to Excel');
    } catch (err) {
      console.error(err);
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8" style={{ fontFamily: 'Public Sans, sans-serif' }}>
      <div className="flex flex-wrap justify-between items-end gap-3 mb-6">
        <div>
          <h1
            className="text-3xl font-bold"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Transactions
          </h1>
          <p className="text-gray-600 text-sm mt-1">
            All payments, settlements and earnings across restaurants, users
            and delivery agents.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={resetFilters}
            data-testid="txn-reset-filters"
          >
            <RotateCcw size={16} className="mr-1" /> Reset
          </Button>
          <Button
            onClick={exportExcel}
            disabled={exporting || total === 0}
            style={{ backgroundColor: '#C2410C' }}
            className="text-white"
            data-testid="txn-export-button"
          >
            {exporting ? (
              <Loader2 className="animate-spin mr-1" size={16} />
            ) : (
              <Download size={16} className="mr-1" />
            )}
            Export Excel
          </Button>
        </div>
      </div>

      {/* FILTER BAR */}
      <Card className="mb-4">
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-7 gap-3">
          <div>
            <Label className="text-xs">Restaurant</Label>
            <Select
              value={restaurantId}
              onValueChange={(v) => {
                setRestaurantId(v);
                setPage(1);
              }}
            >
              <SelectTrigger data-testid="txn-filter-restaurant">
                <SelectValue placeholder="All restaurants" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All restaurants</SelectItem>
                {restaurants.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs">User</Label>
            <Select
              value={userId}
              onValueChange={(v) => {
                setUserId(v);
                setPage(1);
              }}
            >
              <SelectTrigger data-testid="txn-filter-user">
                <SelectValue placeholder="All users" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All users</SelectItem>
                {users.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.name} {u.phone ? `(${u.phone})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs">Delivery Agent</Label>
            <Select
              value={partnerID}
              onValueChange={(v) => {
                setPartnerID(v);
                setPage(1);
              }}
            >
              <SelectTrigger data-testid="txn-filter-agent">
                <SelectValue placeholder="All agents" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All agents</SelectItem>
                {agents.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.name} {a.phone ? `(${a.phone})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs">Type</Label>
            <Select
              value={type}
              onValueChange={(v) => {
                setType(v);
                setPage(1);
              }}
            >
              <SelectTrigger data-testid="txn-filter-type">
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {TYPES.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs">Status</Label>
            <Select
              value={status}
              onValueChange={(v) => {
                setStatus(v);
                setPage(1);
              }}
            >
              <SelectTrigger data-testid="txn-filter-status">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs">From</Label>
            <Input
              type="date"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
                setPage(1);
              }}
              data-testid="txn-filter-from"
            />
          </div>

          <div>
            <Label className="text-xs">To</Label>
            <Input
              type="date"
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
                setPage(1);
              }}
              data-testid="txn-filter-to"
            />
          </div>
        </CardContent>
      </Card>

      {/* TABLE */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center items-center py-16">
              <Loader2 className="animate-spin text-orange-600" size={28} />
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              No transactions match the current filters.
            </div>
          ) : (
            <Table data-testid="txn-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Order #</TableHead>
                  <TableHead>Restaurant</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Agent</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((t) => (
                  <TableRow
                    key={t.id}
                    data-testid={`txn-row-${t.id}`}
                    className="hover:bg-gray-50"
                  >
                    <TableCell className="whitespace-nowrap text-xs text-gray-600">
                      {t.createdAt
                        ? format(new Date(t.createdAt), 'dd MMM yyyy HH:mm')
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={`text-[10px] ${TYPE_COLORS[t.type] ||
                          'bg-gray-100 text-gray-700 border-gray-200'} border`}
                      >
                        {t.type || '—'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs">
                      {t.orderNumber || (
                        <span className="text-gray-400">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {t.restaurant?.name || (
                        <span className="text-gray-400">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {t.user?.name || t.user?.phone || (
                        <span className="text-gray-400">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {t.agent?.name || (
                        <span className="text-gray-400">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">
                      {t.paymentMethod || '—'}
                    </TableCell>
                    <TableCell className="text-right font-semibold">
                      {formatINR(t.amount)}
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={`text-[10px] border ${
                          STATUS_COLORS[t.status] ||
                          'bg-gray-100 text-gray-700 border-gray-200'
                        }`}
                      >
                        {t.status || '—'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* PAGINATION */}
      <div className="flex justify-between items-center mt-4 text-sm text-gray-600">
        <div data-testid="txn-pagination-summary">
          Showing{' '}
          <span className="font-semibold text-gray-900">
            {rows.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1}–
            {(page - 1) * PAGE_SIZE + rows.length}
          </span>{' '}
          of <span className="font-semibold text-gray-900">{total}</span>
        </div>
        <div className="flex gap-2 items-center">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            data-testid="txn-prev-page"
          >
            Prev
          </Button>
          <span className="px-2">
            Page <span className="font-semibold">{page}</span> / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            data-testid="txn-next-page"
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Transactions;
